import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FilterLabel, Sidenav } from '../style'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Checkbox } from 'rsuite'
import { IStartup } from 'domain/feature/hub/company/Ecosystem/contracts'
import { ValueType } from 'rsuite/esm/Checkbox'

interface FilterDataItem {
  startup_info_activity: string
  business_model: string
  startup_info_target: string | null
  company_address_state: string
  number_employees: string
  startup_info_development_stage: number | null
  startup_info_has_raised_investment: boolean
  startup_info_investment_source: string
}

export type EcosystemFilterDatasetRow = FilterDataItem | IStartup

const isStartupFilterRow = (row: EcosystemFilterDatasetRow): row is IStartup =>
  'startupInfo' in row && row.startupInfo != null

const rowCompanyAddressState = (row: EcosystemFilterDatasetRow): string | null | undefined =>
  isStartupFilterRow(row) ? row.address?.state : row.company_address_state

const rowStartupActivity = (row: EcosystemFilterDatasetRow): string | null | undefined =>
  isStartupFilterRow(row) ? row.startupInfo.activity : row.startup_info_activity

const rowBusinessModel = (row: EcosystemFilterDatasetRow): string | null | undefined =>
  isStartupFilterRow(row) ? row.startupInfo.businessModel : row.business_model

const rowTarget = (row: EcosystemFilterDatasetRow): string | null | undefined =>
  isStartupFilterRow(row) ? row.startupInfo.target : row.startup_info_target ?? undefined

const rowInvestmentSource = (row: EcosystemFilterDatasetRow): string | null | undefined =>
  isStartupFilterRow(row) ? row.startupInfo.investmentSource : row.startup_info_investment_source

const rowNumberEmployees = (row: EcosystemFilterDatasetRow): string | null | undefined =>
  isStartupFilterRow(row) ? row.startupInfo.numberEmployees : row.number_employees

interface FilterCounts {
  address_state: number
  activity: number
  business_model: number
  number_employees: number
  target: number
  investment_source: number
  development_stage: number
  has_raised_investment: number
}

export interface MultiFilterProps {
  isExpanded: boolean
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>
  startups: IStartup[]
  filterData?: FilterDataItem[]
  onFilterChange: (type: string, values: ValueType[], checked: boolean) => void
  filterCounts: FilterCounts
  selectedFilters?: Record<string, string[]>
  /** Quando definido, o painel é renderizado dentro deste elemento com o canto direito alinhado à direita da barra (uso na página ecosystem). */
  filterBarRef?: React.RefObject<HTMLDivElement | null>
}

const FilterGroupTitle = ({ children, onClear }: { children: React.ReactNode, onClear: () => void }): JSX.Element => (
  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
    <span style={{ color: '#464646', fontWeight: 500, fontSize: 16, flex: 1 }}>{children}</span>
    <span
      onClick={onClear}
      style={{
        color: '#0066B3',
        fontWeight: 400,
        fontSize: 14,
        textDecoration: 'underline',
        cursor: 'pointer',
        userSelect: 'none',
        marginLeft: 12
      }}
      title="Limpar filtro"
    >
      <FontAwesomeIcon icon={['fas', 'times']} style={{ fontSize: 14 }} />
    </span>
  </div>
)

export const MultiFilter = ({ setIsExpanded, onFilterChange, startups, filterData, filterCounts, selectedFilters = {}, filterBarRef }: MultiFilterProps): JSX.Element => {
  const componentRef = useRef<HTMLDivElement>(null)
  const alignToBarRight = filterBarRef != null

  /** Prefer explicit filter GET payload; otherwise lista atual — ambos podem ser snake_case ou IStartup. */
  const optionRows: EcosystemFilterDatasetRow[] = filterData ?? (startups as EcosystemFilterDatasetRow[])

  const filterValidValues = (values: Array<string | null | undefined>): string[] => {
    return Array.from(new Set(values.filter((v): v is string => typeof v === 'string' && v.length > 0 && v !== '' && v !== '""')))
  }

  const sortNumberEmployees = (employees: string[]): string[] => {
    return employees.sort((a, b) => {
      const getFirstNumber = (str: string): number => {
        const match = str.match(/\d+/)
        return match !== null ? parseInt(match[0], 10) : 0
      }

      const numA = getFirstNumber(a)
      const numB = getFirstNumber(b)

      return numA - numB
    })
  }

  const states = filterValidValues(optionRows.map(row => rowCompanyAddressState(row)))

  const startupActivities = filterValidValues(optionRows.map(row => rowStartupActivity(row)))

  const businessModels = filterValidValues(optionRows.map(row => rowBusinessModel(row)))

  const targets = filterValidValues(optionRows.map(row => rowTarget(row)))

  const investmentSources = filterValidValues(optionRows.map(row => rowInvestmentSource(row)))

  const numberEmployees = sortNumberEmployees(filterValidValues(optionRows.map(row => rowNumberEmployees(row))))

  const developmentStage = [{ id: 1, label: 'Ainda não realizei vendas do produto' }, { id: 2, label: 'Já realizei a primeira venda' }, { id: 3, label: 'Já tenho vendas recorrentes' }]
  const hasRaisedInvestment = [{ value: true, label: 'Sim' }, { value: false, label: 'Não' }]

  const selectedStates = selectedFilters.company_address_state ?? []
  const selectedBusinessModels = selectedFilters.business_model ?? []
  const selectedTargets = selectedFilters.startup_info_target ?? []
  const selectedStartupActivities = selectedFilters.startup_info_activity ?? []
  const selectedInvestmentSources = selectedFilters.startup_info_investment_source ?? []
  const selectedNumberEmployees = selectedFilters.number_employees ?? []
  const selectedDevelopmentStages = (selectedFilters.startup_info_development_stage ?? []).map(String)
  const selectedHasRaisedInvestments = (selectedFilters.startup_info_has_raised_investment ?? []).map(String)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node
      const isInsideDropdown = componentRef.current !== null && componentRef.current.contains(target)
      const isInsideBar = alignToBarRight && filterBarRef?.current != null && filterBarRef.current.contains(target)
      if (!isInsideDropdown && !isInsideBar) {
        setIsExpanded(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [setIsExpanded, alignToBarRight, filterBarRef])

  const divider = <div style={{ borderBottom: '1px solid #eee', margin: '12px 0' }} />

  const panelStyle: React.CSSProperties = {
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: 400,
    width: 320,
    background: 'white',
    borderRadius: 8,
    boxShadow: '0 2px 16px rgba(0,0,0,0.12)',
    padding: 24,
    position: 'absolute',
    zIndex: 1000,
    marginTop: 8
  }
  if (alignToBarRight) {
    panelStyle.right = 0
    panelStyle.top = '100%'
    panelStyle.left = 'auto'
  } else {
    panelStyle.left = 0
    panelStyle.top = '100%'
  }

  const handleClearFilters = (): void => {
    onFilterChange('company_address_state', [], false)
    onFilterChange('startup_info_activity', [], false)
    onFilterChange('business_model', [], false)
    onFilterChange('number_employees', [], false)
    onFilterChange('startup_info_target', [], false)
    onFilterChange('startup_info_investment_source', [], false)
    onFilterChange('startup_info_development_stage', [], false)
    onFilterChange('startup_info_has_raised_investment', [], false)
  }

  const panelContent = (
    <div ref={componentRef} style={panelStyle}>
      <div id='Header' style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: 0, marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0066B3', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Filtros</h2>
        <span
          onClick={handleClearFilters}
          style={{
            color: '#0066B3',
            fontWeight: 400,
            fontSize: 14,
            textDecoration: 'underline',
            cursor: 'pointer',
            userSelect: 'none',
            marginLeft: 12
          }}
        >
          Limpar
        </span>
      </div>
      <FilterGroupTitle onClear={() => onFilterChange('startup_info_activity', [], false)}>
        Vertical/Setor{filterCounts.activity > 0 ? ` (${filterCounts.activity})` : ''}
      </FilterGroupTitle>
      <div id='checkboxesMarket' style={{ marginBottom: '16px' }}>
        {startupActivities.map((startupActivity, index) => (
          <div key={`bm-${index}`} style={{ marginBottom: '8px' }}>
            <Checkbox
              checked={selectedStartupActivities.includes(startupActivity)}
              value={startupActivity}
              onChange={(value, checked) => {
                if (value !== undefined) {
                  let newActivities = [...selectedStartupActivities]
                  if (checked) {
                    newActivities.push(String(value))
                  } else {
                    newActivities = newActivities.filter(item => item !== String(value))
                  }
                  onFilterChange('startup_info_activity', newActivities, newActivities.length > 0)
                }
              }}
            >{` ${startupActivity}`}</Checkbox>
          </div>
        ))}
      </div>
      {divider}
      <FilterGroupTitle onClear={() => onFilterChange('business_model', [], false)}>
        Modelo de Negócio{filterCounts.business_model > 0 ? ` (${filterCounts.business_model})` : ''}
      </FilterGroupTitle>
      <div id='checkboxesBusinessModel' style={{ marginBottom: '16px' }}>
        {businessModels.map((businessModel, index) => (
          <div key={`bm-${index}`} style={{ marginBottom: '8px' }}>
            <Checkbox
              checked={selectedBusinessModels.includes(businessModel)}
              value={businessModel}
              onChange={(value, checked) => {
                if (value !== undefined) {
                  let newModels = [...selectedBusinessModels]
                  if (checked) {
                    newModels.push(String(value))
                  } else {
                    newModels = newModels.filter(item => item !== String(value))
                  }
                  onFilterChange('business_model', newModels, newModels.length > 0)
                }
              }}
            >{` ${businessModel}`}</Checkbox>
          </div>
        ))}
      </div>
      {divider}
      <FilterGroupTitle onClear={() => onFilterChange('startup_info_target', [], false)}>
        Público alvo{filterCounts.target > 0 ? ` (${filterCounts.target})` : ''}
      </FilterGroupTitle>
      <div id='checkboxesTarget' style={{ marginBottom: '16px' }}>
        {targets.map((target, index) => (
          <div key={`target-${index}`} style={{ marginBottom: '8px' }}>
            <Checkbox
              checked={selectedTargets.includes(target)}
              value={target}
              onChange={(value, checked) => {
                if (value !== undefined) {
                  let newTargets = [...selectedTargets]
                  if (checked) {
                    newTargets.push(String(value))
                  } else {
                    newTargets = newTargets.filter(item => item !== String(value))
                  }
                  onFilterChange('startup_info_target', newTargets, newTargets.length > 0)
                }
              }}
            >{` ${target}`}</Checkbox>
          </div>
        ))}
      </div>
      {divider}
      <FilterGroupTitle onClear={() => onFilterChange('company_address_state', [], false)}>
        Estado{filterCounts.address_state > 0 ? ` (${filterCounts.address_state})` : ''}
      </FilterGroupTitle>
      <div id='checkboxesStates' style={{ marginBottom: '16px' }}>
        {states.map((state, index) => (
          <div key={`bm-${index}`} style={{ marginBottom: '8px' }}>
            <Checkbox
              checked={selectedStates.includes(state)}
              value={state}
              onChange={(value, checked) => {
                if (value !== undefined) {
                  let newStates = [...selectedStates]
                  if (checked) {
                    newStates.push(String(value))
                  } else {
                    newStates = newStates.filter(item => item !== String(value))
                  }
                  onFilterChange('company_address_state', newStates, newStates.length > 0)
                }
              }}
            >{` ${state}`}</Checkbox>
          </div>
        ))}
      </div>
      {divider}
      <FilterGroupTitle onClear={() => onFilterChange('number_employees', [], false)}>
        Número de Colaboradores{filterCounts.number_employees > 0 ? ` (${filterCounts.number_employees})` : ''}
      </FilterGroupTitle>
      <div id='checkboxesNumberEmployees' style={{ marginBottom: '16px' }}>
        {numberEmployees.map((numberEmployee, index) => (
          <div key={`bm-${index}`} style={{ marginBottom: '8px' }}>
            <Checkbox
              checked={selectedNumberEmployees.includes(numberEmployee)}
              value={numberEmployee}
              onChange={(value, checked) => {
                if (value !== undefined) {
                  let newEmployees = [...selectedNumberEmployees]
                  if (checked) {
                    newEmployees.push(String(value))
                  } else {
                    newEmployees = newEmployees.filter(item => item !== String(value))
                  }
                  onFilterChange('number_employees', newEmployees, newEmployees.length > 0)
                }
              }}
            >{` ${numberEmployee} funcionários`}</Checkbox>
          </div>
        ))}
      </div>
      {divider}
      <FilterGroupTitle onClear={() => onFilterChange('startup_info_development_stage', [], false)}>
        Estágio de Desenvolvimento{filterCounts.development_stage > 0 ? ` (${filterCounts.development_stage})` : ''}
      </FilterGroupTitle>
      <div id='checkboxesDevelopmentStage' style={{ marginBottom: '16px' }}>
        {developmentStage.map((stage, index) => (
          <div key={`bm-${index}`} style={{ marginBottom: '8px' }}>
            <Checkbox
              checked={selectedDevelopmentStages.includes(String(stage.id))}
              value={stage.id}
              onChange={(value, checked) => {
                if (value !== undefined) {
                  let newStages = [...selectedDevelopmentStages]
                  if (checked) {
                    newStages.push(String(value))
                  } else {
                    newStages = newStages.filter(item => item !== String(value))
                  }
                  onFilterChange('startup_info_development_stage', newStages, newStages.length > 0)
                }
              }}
            >{` ${stage.label}`}</Checkbox>
          </div>
        ))}
      </div>
      {divider}
      <FilterGroupTitle onClear={() => onFilterChange('startup_info_has_raised_investment', [], false)}>
        Investimento Captado{filterCounts.has_raised_investment > 0 ? ` (${filterCounts.has_raised_investment})` : ''}
      </FilterGroupTitle>
      <div id='checkboxeshasRaisedInvestment' style={{ marginBottom: '16px' }}>
        {hasRaisedInvestment.map((investment, index) => (
          <div key={`bm-${index}`} style={{ marginBottom: '8px' }}>
            <Checkbox
              checked={selectedHasRaisedInvestments.includes(String(investment.value))}
              value={investment.value as unknown as ValueType}
              onChange={(value, checked) => {
                if (value !== undefined) {
                  let newInvestments = [...selectedHasRaisedInvestments]
                  if (checked) {
                    newInvestments.push(String(value))
                  } else {
                    newInvestments = newInvestments.filter(item => item !== String(value))
                  }
                  onFilterChange('startup_info_has_raised_investment', newInvestments, newInvestments.length > 0)
                }
              }}
            >{` ${investment.label}`}</Checkbox>
          </div>
        ))}
      </div>
      {divider}
      <FilterGroupTitle onClear={() => onFilterChange('startup_info_investment_source', [], false)}>
        Origem do investimento{filterCounts.investment_source > 0 ? ` (${filterCounts.investment_source})` : ''}
      </FilterGroupTitle>
      <div id='checkboxesInvestmentSource' style={{ marginBottom: '16px' }}>
        {investmentSources.map((source, index) => (
          <div key={`investmentSource-${index}`} style={{ marginBottom: '8px' }}>
            <Checkbox
              checked={selectedInvestmentSources.includes(source)}
              value={source}
              onChange={(value, checked) => {
                if (value !== undefined) {
                  let newSources = [...selectedInvestmentSources]
                  if (checked) {
                    newSources.push(String(value))
                  } else {
                    newSources = newSources.filter(item => item !== String(value))
                  }
                  onFilterChange('startup_info_investment_source', newSources, newSources.length > 0)
                }
              }}
            >{` ${source}`}</Checkbox>
          </div>
        ))}
      </div>
      {divider}
    </div>
  )

  if (alignToBarRight && filterBarRef?.current != null) {
    return createPortal(panelContent, filterBarRef.current)
  }
  return panelContent
}
